import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { errorMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Minus, GitCompareArrows, Scissors, Tag, Loader2, Hash, Clock, Link2 } from "lucide-react";

const esModif = (l) => l.cambia_opcional || l.cambia_cantidad || l.cambia_frt || l.cambia_mano_obra;
const esCambio = (l) => l.estado === "agregado" || l.estado === "quitado" || esModif(l);
const tieneHerencia = (h) => (h.herencia?.agregar?.length > 0) || (h.herencia?.quitar?.length > 0);

export const PropagacionDialog = ({ open, onClose, titulo, grupos, aplicarFn, onDone,
                                    loading = false, loadingText, error = null }) => {
  const [selHijas, setSelHijas] = useState(() => new Set());
  const [excl, setExcl] = useState(() => new Set()); // líneas de cambio desmarcadas
  const [guardando, setGuardando] = useState(false);

  const lineaKey = (mid, hid, l) => esModif(l)
    ? `chg|${hid}|${l.cant_key || l.opc_key || (l.cambia_frt ? "frt" : l.cambia_mano_obra ? "mo" : l.codigo)}`
    : `${mid}|${hid}|${l.estado}|${l.codigo}`;
  const splitKey = (hid, si, l) => `split|${hid}|${si}|${l.codigo}`;
  const herenciaKey = (hid) => `her|${hid}`;

  useEffect(() => {
    if (!open) return;
    const s = new Set();
    (grupos || []).forEach((g) => g.hijas.forEach((h) => s.add(`${g.maestra_id}|${h.hija_id}`)));
    setSelHijas(s); setExcl(new Set());
  }, [open, grupos]);

  const toggleHija = (key) => setSelHijas((p) => { const n = new Set(p); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleLinea = (k) => setExcl((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });

  // Cambios activos = hija seleccionada && línea no excluida.
  const totalActivos = useMemo(() => {
    let c = 0;
    (grupos || []).forEach((g) => g.hijas.forEach((h) => {
      if (!selHijas.has(`${g.maestra_id}|${h.hija_id}`)) return;
      h.lineas.forEach((l) => { if (esCambio(l) && !excl.has(lineaKey(g.maestra_id, h.hija_id, l))) c += 1; });
      (h.split_nuevas || []).forEach((sn, si) =>
        sn.lineas.forEach((l) => { if (!excl.has(splitKey(h.hija_id, si, l))) c += 1; }));
      if (tieneHerencia(h) && !excl.has(herenciaKey(h.hija_id))) c += 1;
    }));
    return c;
  }, [grupos, selHijas, excl]);

  const totalHijas = (grupos || []).reduce((a, g) => a + g.hijas.length, 0);

  const aplicar = async () => {
    const seleccion = [];
    (grupos || []).forEach((g) => g.hijas.forEach((h) => {
      if (!selHijas.has(`${g.maestra_id}|${h.hija_id}`)) return;
      const omitir_agregar = [], omitir_quitar = [], omitir_opcional = [], omitir_cantidad = [];
      let omitir_frt = false, omitir_mano_obra = false;
      let activos = 0;
      h.lineas.forEach((l) => {
        if (!esCambio(l)) return;
        const off = excl.has(lineaKey(g.maestra_id, h.hija_id, l));
        if (off) {
          if (l.cambia_opcional) omitir_opcional.push(l.opc_key);
          if (l.cambia_cantidad) omitir_cantidad.push(l.cant_key);
          if (l.cambia_frt) omitir_frt = true;
          if (l.cambia_mano_obra) omitir_mano_obra = true;
          if (l.estado === "agregado") omitir_agregar.push(l.codigo);
          else if (l.estado === "quitado") omitir_quitar.push(l.codigo);
        } else activos += 1;
      });
      const omitir_herencia = tieneHerencia(h) && excl.has(herenciaKey(h.hija_id));
      if (tieneHerencia(h) && !omitir_herencia) activos += 1;
      if (activos > 0 || h.split) {
        const split_omitir = (h.split_nuevas || []).map((sn, si) =>
          sn.lineas.filter((l) => excl.has(splitKey(h.hija_id, si, l))).map((l) => l.codigo));
        seleccion.push({ maestra_id: g.maestra_id, hija_id: h.hija_id, omitir_agregar, omitir_quitar,
          omitir_opcional, omitir_cantidad, omitir_frt, omitir_mano_obra, omitir_herencia, split_omitir });
      }
    }));
    if (seleccion.length === 0) { toast.error("No hay cambios marcados para aplicar"); return; }
    setGuardando(true);
    try {
      const { data } = await aplicarFn(seleccion);
      toast.success(`${data.actualizadas} plantilla(s) hija(s) actualizada(s)`);
      onClose(); onDone && onDone();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="propagacion-dialog" className="bg-white max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <GitCompareArrows size={18} className="text-indigo-600" /> {titulo || "Actualizar plantillas hijas"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Revisá el antes/después por plantilla. <span className="text-emerald-700 font-medium">Verde</span> = se agrega,
            <span className="text-rose-700 font-medium"> rojo tachado</span> = se quita. Desmarcá una línea para NO aplicar ese cambio puntual.
            Los repuestos fijos y agregados a mano no se tocan.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
          {loading && (
            <div data-testid="propagacion-cargando" className="flex flex-col items-center justify-center gap-3 py-16 text-center">
              <Loader2 size={34} className="animate-spin text-indigo-600" />
              <p className="text-sm text-slate-600">{loadingText || "Calculando cambios… esto puede tardar unos segundos."}</p>
            </div>
          )}
          {!loading && error && (
            <p data-testid="propagacion-error" className="py-12 text-center text-sm text-rose-600">{error}</p>
          )}
          {!loading && !error && totalHijas === 0 && (
            <p data-testid="propagacion-sin-cambios" className="py-10 text-center text-sm text-muted-foreground">
              No hay cambios pendientes: las plantillas generadas ya están al día.
            </p>
          )}
          {!loading && !error && (grupos || []).map((g) => (
            <div key={g.maestra_id} className="space-y-2">
              {grupos.length > 1 && <p className="text-xs font-semibold text-slate-500 uppercase">{g.maestra_nombre}</p>}
              {g.hijas.map((h) => {
                const hkey = `${g.maestra_id}|${h.hija_id}`;
                const hijaOn = selHijas.has(hkey);
                return (
                  <div key={h.hija_id} data-testid={`propagacion-hija-${h.hija_id}`}
                    className="rounded-sm border border-slate-200 p-2.5">
                    <label className="flex items-center gap-2 cursor-pointer mb-1.5">
                      <Checkbox data-testid={`propagacion-sel-${h.hija_id}`} checked={hijaOn} onCheckedChange={() => toggleHija(hkey)} />
                      <span className="font-semibold text-sm flex-1">{h.nombre}</span>
                      {h.agregados > 0 && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-sm">+{h.agregados}</span>}
                      {h.quitados > 0 && <span className="text-[10px] font-semibold text-rose-700 bg-rose-100 px-1.5 py-0.5 rounded-sm">−{h.quitados}</span>}
                      {h.opcionales > 0 && <span data-testid={`propagacion-opcionales-${h.hija_id}`} className="text-[10px] font-semibold text-sky-700 bg-sky-100 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5"><Tag size={10} />~{h.opcionales}</span>}
                      {h.cantidades > 0 && <span data-testid={`propagacion-cantidades-${h.hija_id}`} className="text-[10px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5"><Hash size={10} />{h.cantidades}</span>}
                      {h.frts > 0 && <span data-testid={`propagacion-frts-${h.hija_id}`} className="text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5"><Clock size={10} />FRT</span>}
                      {tieneHerencia(h) && <span data-testid={`propagacion-herencias-${h.hija_id}`} className="text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-100 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5"><Link2 size={10} />herencia</span>}
                      {h.split && <span data-testid={`propagacion-split-${h.hija_id}`} className="text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-sm flex items-center gap-1"><Scissors size={10} /> se divide en {(h.split_nuevas?.length || 0) + 1}</span>}
                    </label>
                    {h.modelos?.length > 0 && <p className="text-[10px] text-muted-foreground mb-1 pl-6">{h.modelos.join(", ")}</p>}
                    {(h.mano_obra_efectiva != null) && (
                      <p data-testid={`propagacion-mo-${h.hija_id}`} className="text-[10px] text-teal-700 mb-1 pl-6">
                        Mano de obra efectiva (con herencia): <b>₡{Number(h.mano_obra_efectiva).toLocaleString("es-CR")}</b>
                        {h.frt_efectiva > 0 && <> · FRT <b>{h.frt_efectiva}</b></>}
                      </p>
                    )}
                    <div className={`space-y-0.5 pl-2 ${hijaOn ? "" : "opacity-40 pointer-events-none"}`}>
                      {h.lineas.map((l, i) => {
                        const cambio = esCambio(l);
                        const k = lineaKey(g.maestra_id, h.hija_id, l);
                        const on = !excl.has(k);
                        const cls = l.estado === "agregado" ? "text-emerald-700 bg-emerald-50"
                          : l.estado === "quitado" ? "text-rose-700 bg-rose-50"
                          : l.cambia_cantidad ? "text-violet-700 bg-violet-50"
                          : l.cambia_opcional ? "text-sky-700 bg-sky-50"
                          : (l.cambia_frt || l.cambia_mano_obra) ? "text-amber-700 bg-amber-50" : "text-slate-600";
                        const textCls = l.estado === "quitado" && on ? "line-through" : "";
                        return (
                          <div key={i} className={`flex items-center gap-2 px-2 py-0.5 rounded-sm text-xs ${cls} ${cambio && !on ? "opacity-45" : ""}`}>
                            {cambio ? (
                              <Checkbox data-testid={`propagacion-linea-${h.hija_id}-${esModif(l) ? "chg" : l.estado}-${l.codigo || (l.cambia_frt ? "frt" : l.cambia_mano_obra ? "mo" : "")}`}
                                className="h-3.5 w-3.5" checked={on} onCheckedChange={() => toggleLinea(k)} />
                            ) : <span className="w-3.5" />}
                            {l.estado === "agregado" ? <Plus size={11} /> : l.estado === "quitado" ? <Minus size={11} /> : l.cambia_cantidad ? <Hash size={11} /> : l.cambia_opcional ? <Tag size={11} /> : (l.cambia_frt || l.cambia_mano_obra) ? <Clock size={11} /> : <span className="w-[11px]" />}
                            <span className={`font-mono font-semibold ${textCls}`}>{l.codigo || "—"}</span>
                            <span className={`flex-1 truncate ${textCls}`}>{l.nombre}</span>
                            {l.cambia_cantidad && <span className="text-[9px] uppercase font-semibold bg-violet-200 text-violet-800 px-1 rounded-sm">cantidad {l.cantidad_actual} → {l.cantidad_nueva}</span>}
                            {l.cambia_opcional && <span className="text-[9px] uppercase font-semibold bg-sky-200 text-sky-800 px-1 rounded-sm">{l.opcional_nuevo ? "marcar opcional" : "marcar obligatorio"}</span>}
                            {l.cambia_frt && <span className="text-[9px] uppercase font-semibold bg-amber-200 text-amber-800 px-1 rounded-sm">FRT {l.frt_actual} → {l.frt_nuevo}</span>}
                            {l.cambia_mano_obra && <span className="text-[9px] uppercase font-semibold bg-amber-200 text-amber-800 px-1 rounded-sm">₡{l.mano_obra_actual} → ₡{l.mano_obra_nuevo}</span>}
                            {l.fijo && <span className="text-[9px] uppercase font-semibold bg-indigo-200 text-indigo-800 px-1 rounded-sm">fijo</span>}
                            {l.manual && l.estado === "igual" && <span className="text-[9px] uppercase font-semibold bg-zinc-200 text-zinc-700 px-1 rounded-sm">manual</span>}
                            {cambio && !on && <span className="text-[9px] uppercase font-semibold bg-zinc-200 text-zinc-700 px-1 rounded-sm">omitido</span>}
                            {l.cantidad != null && <span className="tabular-nums">×{l.cantidad}</span>}
                          </div>
                        );
                      })}
                    </div>
                    {tieneHerencia(h) && (
                      <div className={`space-y-0.5 pl-2 mt-0.5 ${hijaOn ? "" : "opacity-40 pointer-events-none"}`}>
                        {[...(h.herencia.agregar || []).map((p) => ({ ...p, modo: "agregar" })),
                          ...(h.herencia.quitar || []).map((p) => ({ ...p, modo: "quitar" }))].map((p, i) => {
                          const hk = herenciaKey(h.hija_id);
                          const on = !excl.has(hk);
                          return (
                            <div key={`${p.modo}-${p.id}-${i}`}
                              className={`flex items-center gap-2 px-2 py-0.5 rounded-sm text-xs ${p.modo === "agregar" ? "text-emerald-700 bg-emerald-50" : "text-rose-700 bg-rose-50"} ${!on ? "opacity-45" : ""}`}>
                              {i === 0 ? (
                                <Checkbox data-testid={`propagacion-herencia-${h.hija_id}`} className="h-3.5 w-3.5"
                                  checked={on} onCheckedChange={() => toggleLinea(hk)} />
                              ) : <span className="w-3.5" />}
                              {p.modo === "agregar" ? <Plus size={11} /> : <Minus size={11} />}
                              <span className="flex-1 truncate">
                                Herencia: se {p.modo === "agregar" ? "agrega" : "quita"} «{p.nombre}»
                                {p.frt > 0 && <> ({p.modo === "agregar" ? "+" : "−"}{p.frt} h de mano de obra)</>}
                              </span>
                              {!on && <span className="text-[9px] uppercase font-semibold bg-zinc-200 text-zinc-700 px-1 rounded-sm">omitido</span>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {h.split && (h.split_nuevas || []).length > 0 && (
                      <div data-testid={`propagacion-split-nuevas-${h.hija_id}`} className="mt-2 pl-2 space-y-2">
                        <p className="text-[10px] font-semibold text-amber-800 flex items-center gap-1">
                          <Scissors size={11} /> Modelos con repuestos distintos → se crearán plantillas separadas:
                        </p>
                        {h.split_nuevas.map((sn, si) => (
                          <div key={si} className="rounded-sm border border-amber-200 bg-amber-50/40 p-2">
                            <p className="text-[11px] font-semibold text-slate-700 mb-1">{sn.modelos.join(", ")}</p>
                            <div className="space-y-0.5">
                              {sn.lineas.map((l, li) => {
                                const sk = splitKey(h.hija_id, si, l);
                                const on = !excl.has(sk);
                                return (
                                  <div key={li} className={`flex items-center gap-2 px-2 py-0.5 rounded-sm text-xs text-emerald-700 bg-emerald-50 ${on ? "" : "opacity-45"}`}>
                                    <Checkbox data-testid={`propagacion-split-linea-${h.hija_id}-${si}-${l.codigo}`}
                                      className="h-3.5 w-3.5" checked={on} onCheckedChange={() => toggleLinea(sk)} />
                                    <Plus size={11} />
                                    <span className={`font-mono font-semibold ${on ? "" : "line-through"}`}>{l.codigo || "—"}</span>
                                    <span className={`flex-1 truncate ${on ? "" : "line-through"}`}>{l.nombre}</span>
                                    {l.fijo && <span className="text-[9px] uppercase font-semibold bg-indigo-200 text-indigo-800 px-1 rounded-sm">fijo</span>}
                                    {!on && <span className="text-[9px] uppercase font-semibold bg-zinc-200 text-zinc-700 px-1 rounded-sm">omitido</span>}
                                    <span className="tabular-nums">×{l.cantidad}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" data-testid="propagacion-cancelar" onClick={onClose}>Cancelar</Button>
          <Button data-testid="propagacion-aplicar" onClick={aplicar} disabled={loading || guardando || totalActivos === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {guardando ? "Aplicando…" : loading ? "Calculando…" : `Aplicar (${totalActivos})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
